import logo from "./Assets/cube.jpeg";
import supercare from "./Assets/sc-logo.png";
import { useAuth } from "./authContext";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AIPage = () => {
  const { url, headers, msisdn } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const getChatStorageKey = () => `aiChatMessages_${msisdn }`;

  useEffect(() => {
    if (!msisdn) return; 
    
    const chatKey = getChatStorageKey();
    const savedMessages = sessionStorage.getItem(chatKey);
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages);
      } catch (error) {
        console.error("Error parsing saved messages:", error);
        initializeDefaultMessage();
      }
    } else {
      initializeDefaultMessage();
    }
  }, [msisdn]);

  const initializeDefaultMessage = () => {
    const defaultMessage = {
      id: 1,
      text: "Hello! I'm your WellnessAI assistant. 🤖 How can I help you today? You can ask me about mental health, sexual health, or take an assessment.",
      isAI: true,
      timestamp: new Date()
    };
    setMessages([defaultMessage]);
    const chatKey = getChatStorageKey();
    sessionStorage.setItem(chatKey, JSON.stringify([defaultMessage]));
  };

  const saveMessagesToStorage = (newMessages) => {
    try {
      const chatKey = getChatStorageKey();
      sessionStorage.setItem(chatKey, JSON.stringify(newMessages));
    } catch (error) {
      console.error("Error saving messages to session storage:", error);
    }
  };

  const clearChat = () => {
    const chatKey = getChatStorageKey();
    sessionStorage.removeItem(chatKey);
    initializeDefaultMessage();
  };

  const formatTextContent = (content) => {
    if (!content) return content;
    
    let formattedContent = content.replace(/\\n/g, '\n');
    formattedContent = formattedContent.replace(/\\"/g, '"');
    formattedContent = formattedContent.replace(/\\'/g, "'");
    formattedContent = formattedContent.replace(/\\t/g, '    '); 
    formattedContent = formattedContent.replace(/\\r/g, '');
    formattedContent = formattedContent.replace(/\n{3,}/g, '\n\n');
    
    return formattedContent;
  };

  const renderFormattedText = (text) => {
    if (!text) return text;
    
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      const parts = [];
      let currentIndex = 0;
      let partKey = 0;
      
      const boldRegex = /\*\*(.*?)\*\*/g;
      let match;
      
      while ((match = boldRegex.exec(line)) !== null) {
        if (match.index > currentIndex) {
          parts.push(
            <span key={`text-${partKey++}`}>
              {line.substring(currentIndex, match.index)}
            </span>
          );
        }
        
        parts.push(
          <strong key={`bold-${partKey++}`} className="font-bold">
            {match[1]}
          </strong>
        );
        
        currentIndex = match.index + match[0].length;
      }
      
      if (currentIndex < line.length) {
        parts.push(
          <span key={`text-${partKey++}`}>
            {line.substring(currentIndex)}
          </span>
        );
      }
      
      if (parts.length === 0) {
        parts.push(<span key={`line-${lineIndex}`}>{line}</span>);
      }
      
      return (
        <span key={`line-${lineIndex}`}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  const getAIResponse = async (userMessage) => {
    try {
      const response = await fetch(`${url}/wellness/chat`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          text: userMessage
        })
      });

      const result = await response.json();

      if (response.ok && result.success && result.data && result.data.content) {
        let cleanedContent = result.data.content;
        
        cleanedContent = cleanedContent.replace(/\.\.\/[a-zA-Z-]+/g, '');
        cleanedContent = cleanedContent.replace(/\/[a-zA-Z-]+/g, '');
        cleanedContent = cleanedContent.replace(/\s+/g, ' ').trim();
        cleanedContent = cleanedContent.replace(/,\s*\./g, '.');
        cleanedContent = cleanedContent.replace(/\s+,/g, ',');

        cleanedContent = formatTextContent(cleanedContent);

        return {
          text: cleanedContent
        };
      } else {
        return {
          text: "I apologize, but I'm having trouble processing your request right now. Please try again or let me know if you'd like to explore our mental health tools, sexual health education, or take an assessment."
        };
      }
    } catch (error) {
      console.error("AI API Error:", error);
      return {
        text: "I'm experiencing some technical difficulties at the moment. However, I can still help you navigate to our wellness resources. Would you like to explore mental health support, sexual health education, or smart assessments?"
      };
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const newUserMessage = {
      id: Date.now(), 
      text: userInput,
      isAI: false,
      timestamp: new Date()
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    saveMessagesToStorage(updatedMessages);
    
    const currentInput = userInput;
    setUserInput("");
    setIsTyping(true);

    try {
      const aiResponse = await getAIResponse(currentInput);
      const newAIMessage = {
        id: Date.now() + 1, 
        text: aiResponse.text,
        isAI: true,
        timestamp: new Date()
      };

      const finalMessages = [...updatedMessages, newAIMessage];
      setMessages(finalMessages);
      saveMessagesToStorage(finalMessages);
    } catch (error) {
      console.error("Error getting AI response:", error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, I'm having trouble responding right now. Please try again in a moment.",
        isAI: true,
        timestamp: new Date()
      };
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      saveMessagesToStorage(finalMessages);
    } finally {
      setIsTyping(false);
    }
  };

  const handleNavigate = (route) => {
    navigate(route);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-screen bg-white relative overflow-hidden flex flex-col">
      <div className="p-2 sm:p-6 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <img
            src={logo}
            alt="WellnessAI"
            className="w-6 h-6 sm:w-12 sm:h-12 rounded-full shadow-md"
          />
          <span className="text-base sm:text-2xl font-bold text-gray-900">
            WellnessAI
          </span>
        </div>
      </div>

      <div className="flex-1 flex flex-col px-3 sm:px-6 py-2 sm:py-8 min-h-0">
        <div className="text-center mb-3 sm:mb-12 flex-shrink-0">
          <h1 className="text-xl sm:text-4xl md:text-7xl font-extrabold text-black mb-2 sm:mb-4 leading-tight">
            Welcome to WellnessAI
          </h1>
          <p className="text-xs sm:text-lg md:text-2xl text-black font-medium mb-3 sm:mb-8 px-2">
            Your AI-powered companion for better health and wellness
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 max-w-lg mx-auto px-2">
            <button
              onClick={() => handleNavigate('/therapy-tool')}
              className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>🧠</span>
              Mental Health
            </button>
            <button
              onClick={() => handleNavigate('/sexual-health')}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>❤️</span>
              Sexual Health
            </button>
            <button
              onClick={() => handleNavigate('/self-assessment')}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>📊</span>
              Smart Assessment
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 max-w-sm sm:max-w-2xl mx-auto w-full">
          <div className="bg-white rounded-lg sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col flex-1 min-h-0">
            <div className="border-b border-gray-200 p-2 sm:p-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xs sm:text-base font-semibold text-gray-700">Chat with WellnessAI</h3>
              <button
                onClick={clearChat}
                className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md sm:rounded-lg text-xs font-semibold transition-colors duration-300 flex items-center gap-1"
              >
                <span>🗑️</span>
                <span className="hidden sm:inline">Clear Chat</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 sm:p-6 space-y-3 sm:space-y-4 min-h-0">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isAI ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-xs sm:max-w-md px-4 py-3 rounded-xl ${
                      message.isAI
                        ? 'bg-blue-100 text-blue-900 rounded-bl-sm'
                        : 'bg-sky-500 text-white rounded-br-sm'
                    }`}
                  >
                    <div className="text-sm">
                      {message.isAI ? renderFormattedText(message.text) : message.text}
                    </div>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-blue-100 text-blue-900 px-4 py-3 rounded-xl rounded-bl-sm max-w-xs">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce delay-100"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce delay-200"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 p-2 sm:p-6 flex-shrink-0">
              <div className="flex gap-2 sm:gap-3">
                <input
                  type="text"
                  placeholder="Ask me anything about wellness..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl text-base sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  disabled={isTyping}
                  style={{ fontSize: '16px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isTyping}
                  className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-white font-semibold text-xs sm:text-sm transition-all duration-300 ${
                    !userInput.trim() || isTyping
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-sky-500 hover:bg-sky-600 shadow-lg hover:scale-105'
                  }`}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 right-0 p-3 hidden md:block" style={{ zIndex: 50 }}>
        <img 
          src={supercare} 
          alt="Logo" 
          className="w-20 h-30" 
          style={{
            animation: 'flipX 3s linear infinite',
            transformStyle: 'preserve-3d'
          }}
        />
      </div>

      <style>{`
        @keyframes flipX {
          0% {
            transform: rotateY(0deg);
          }
          50% {
            transform: rotateY(180deg);
          }
          100% {
            transform: rotateY(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AIPage;
